import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatRoom } from "amazon-ivs-chat-messaging";
import { chatToken } from "./api";

/**
 * The panel's connection to a show's comments (M0.10).
 *
 * ## Why this is not the customer application's `useChat`
 *
 * It is deliberately a second, smaller implementation, and the reason is that the two rooms are
 * different rooms. The customer's hook carries the whole of spec §4.4 — an anonymous viewer, a
 * missing display name, a mute to explain, a draft to resume across a sign-in overlay — none of
 * which can happen on a panel: whoever is looking at this is signed in with a staff role, or they
 * are not looking at it.
 *
 * What is left is a socket, a capped list and a send, which is small enough that sharing it would
 * mean a package holding an abstraction over two things that only look alike. The same call
 * `apps/customer/src/api/client.ts` makes about the HTTP client, for the same reason.
 *
 * The one place a shared module would genuinely help is the feed buffer, and `append` below is
 * that duplication, named so it is a decision rather than an accident: twelve lines, no
 * dependencies, and if a third caller ever needs it the answer is `packages/ui`.
 */

/** Shorter than the room's, because a moderator scrolling back through 200 lines is not the tool. */
const PANEL_FEED_LIMIT = 120;

const RETRY_MS = [1_000, 2_000, 5_000, 10_000];

export interface PanelComment {
  id: string;
  /** The sender's account id, which is what a mute and a delete are addressed to. */
  userId: string;
  name: string;
  /** Who the sender is. Not the badge — it is what withholds the mute control against staff. */
  role: string | undefined;
  /** What is drawn beside the name (`R-MOD-11`), which is a narrower thing. See `badgeFor`. */
  badge: string | undefined;
  content: string;
  at: Date;
}

export interface RoomChat {
  connected: boolean;
  comments: PanelComment[];
  /** Removes a comment from the panel's own list, once the API has removed it from the room. */
  forget: (messageId: string) => void;
  send: (content: string) => Promise<boolean>;
  maxLength: number;
}

/** See the note above: a deliberate second copy, and the whole of it. */
function append(items: PanelComment[], item: PanelComment): PanelComment[] {
  if (items.some((existing) => existing.id === item.id)) return items;
  const next = [...items, item];
  return next.length > PANEL_FEED_LIMIT
    ? next.slice(next.length - PANEL_FEED_LIMIT)
    : next;
}

export function useRoomChat(publicId: string, live: boolean): RoomChat {
  const [connected, setConnected] = useState(false);
  const [comments, setComments] = useState<PanelComment[]>([]);
  const [maxLength, setMaxLength] = useState(300);
  const roomRef = useRef<ChatRoom | undefined>(undefined);

  useEffect(() => {
    if (!publicId || !live) return;

    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    /** The token already fetched, so a connection mints one rather than two. See the room's copy. */
    let primed: Awaited<ReturnType<typeof chatToken>> | undefined;

    async function connect() {
      // Lazily imported for the reason the broadcast SDK is: the panel's show page is opened for
      // draft shows and ended ones, neither of which has a room to join.
      const { ChatRoom } = await import("amazon-ivs-chat-messaging");
      if (disposed) return;

      let region: string;
      try {
        const first = await chatToken(publicId);
        if (disposed) return;
        region = first.chat.region;
        primed = first;
        setMaxLength(first.chat.maxMessageLength);
      } catch {
        // A 409 while the show is still catching up, or IVS being briefly unreachable. Both fix
        // themselves, and a panel that stopped trying would leave a moderator watching nothing
        // during the minutes that matter most.
        const retry = RETRY_MS[Math.min(failures, RETRY_MS.length - 1)]!;
        failures += 1;
        timer = setTimeout(() => void connect(), retry);
        return;
      }

      failures = 0;
      const room = new ChatRoom({
        regionOrUrl: region,
        // Asked for afresh on every reconnection, so a panel that was open when the show went
        // live does not hold a token minted before there was a room.
        tokenProvider: async () => {
          const answer = primed ?? (await chatToken(publicId));
          primed = undefined;
          return {
            token: answer.chat.token,
            sessionExpirationTime: answer.chat.sessionExpiresAt ?? undefined,
          };
        },
      });
      roomRef.current = room;

      room.addListener("connect", () => setConnected(true));
      room.addListener("connecting", () => setConnected(false));

      room.addListener("message", (message) => {
        setComments((current) =>
          append(current, {
            id: message.id,
            userId: message.sender.userId,
            // From the sender's token attributes, set by this platform when it minted the token —
            // never from the message body (`R-MOD-11`).
            name: message.sender.attributes?.name ?? "",
            role: message.sender.attributes?.role,
            badge: message.sender.attributes?.badge,
            content: message.content,
            at: message.sendTime,
          }),
        );
      });

      // Somebody else deleted it — the other moderator on the show, or an operator. The panel
      // follows the room rather than only its own actions (`R-HOST-31`).
      room.addListener("messageDelete", (event) => {
        setComments((current) => current.filter((one) => one.id !== event.messageId));
      });

      room.addListener("disconnect", () => {
        if (disposed) return;
        setConnected(false);
        const retry = RETRY_MS[Math.min(failures, RETRY_MS.length - 1)]!;
        failures += 1;
        timer = setTimeout(() => void connect(), retry);
      });

      room.connect();
    }

    void connect();

    return () => {
      disposed = true;
      clearTimeout(timer);
      roomRef.current?.disconnect();
      roomRef.current = undefined;
    };
  }, [publicId, live]);

  const forget = useCallback((messageId: string) => {
    setComments((current) => current.filter((one) => one.id !== messageId));
  }, []);

  /**
   * The host talking in their own room.
   *
   * Their message goes through the review handler like anybody's — there is no path to an IVS
   * Chat room that skips it — and passes, because staff are exempt from the link ban and nothing
   * else applies to them. Answers `false` rather than throwing, and the panel says so; a host
   * whose message did not send needs to know before they carry on as though it did.
   */
  const send = useCallback(async (content: string) => {
    const room = roomRef.current;
    if (!room) return false;
    const { SendMessageRequest } = await import("amazon-ivs-chat-messaging");
    try {
      await room.sendMessage(new SendMessageRequest(content));
      return true;
    } catch {
      return false;
    }
  }, []);

  return { connected, comments, forget, send, maxLength };
}
